import React, { useMemo } from "react";
import { RigidBody } from "@react-three/rapier";
import { Grid } from "@react-three/drei";
import { getGrassTexture, getFenceTexture } from "./textures";

const GROUND_SIZE = 80;
const OUTER_SIZE = 220;

const Ground: React.FC = () => {
  const grassNear = useMemo(() => {
    const tex = getGrassTexture().clone();
    tex.needsUpdate = true;
    tex.repeat.set(GROUND_SIZE * 2, GROUND_SIZE * 2);
    return tex;
  }, []);

  const grassFar = useMemo(() => {
    const tex = getGrassTexture().clone();
    tex.needsUpdate = true;
    tex.repeat.set(OUTER_SIZE, OUTER_SIZE);
    return tex;
  }, []);

  const fenceTex = useMemo(() => {
    const tex = getFenceTexture().clone();
    tex.needsUpdate = true;
    tex.repeat.set(GROUND_SIZE / 2, 1);
    return tex;
  }, []);

  return (
    <>
      {/* Decorative far ground so distant mountains/trees don't float over void */}
      <mesh position={[0, -0.15, 0]} receiveShadow>
        <boxGeometry args={[OUTER_SIZE, 0.1, OUTER_SIZE]} />
        <meshStandardMaterial map={grassFar} roughness={1} />
      </mesh>

      <RigidBody type="fixed" colliders="cuboid">
        <mesh position={[0, -0.1, 0]} receiveShadow>
          <boxGeometry args={[GROUND_SIZE, 0.2, GROUND_SIZE]} />
          <meshStandardMaterial map={grassNear} roughness={1} />
        </mesh>
      </RigidBody>

      <Grid
        position={[0, 0.01, 0]}
        args={[GROUND_SIZE, GROUND_SIZE]}
        cellSize={1}
        cellThickness={0.4}
        cellColor="#c4d9a0"
        sectionSize={5}
        sectionThickness={0.8}
        sectionColor="#91bd3a"
        fadeDistance={45}
        fadeStrength={1}
        infiniteGrid={false}
      />

      {/* Boundary walls — tall + deep collider (prevents tunneling) with a visible pixel fence */}
      {[
        { pos: [0, 1.1, -GROUND_SIZE / 2] as [number, number, number], size: [GROUND_SIZE + 2, 2.2, 1.6] as [number, number, number] },
        { pos: [0, 1.1, GROUND_SIZE / 2] as [number, number, number], size: [GROUND_SIZE + 2, 2.2, 1.6] as [number, number, number] },
        { pos: [-GROUND_SIZE / 2, 1.1, 0] as [number, number, number], size: [1.6, 2.2, GROUND_SIZE + 2] as [number, number, number] },
        { pos: [GROUND_SIZE / 2, 1.1, 0] as [number, number, number], size: [1.6, 2.2, GROUND_SIZE + 2] as [number, number, number] },
      ].map((wall, i) => (
        <RigidBody key={i} type="fixed" position={wall.pos} colliders="cuboid">
          <mesh castShadow receiveShadow>
            <boxGeometry args={wall.size} />
            <meshStandardMaterial map={fenceTex} roughness={1} />
          </mesh>
        </RigidBody>
      ))}
    </>
  );
};

export default Ground;
